import { useEffect, useState, useCallback } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractApiErrorMessage } from '../../shared/utils/extract-api-error-message.util';
import { Organization } from '../types/organization.type';
import { ADMIN_ORGANIZATIONS_API_PATHS } from '../constants/organizations-paths.constants';

type UseAdminGetOrganizationByIdResult = {
  isLoading: boolean;
  organization: Organization | null;
  fetchOrganization: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetOrganizationByIdResult, 'fetchOrganization'> = {
  isLoading: true,
  organization: null,
};

export function useAdminGetOrganizationById({
  organizationId,
}: {
  organizationId: string;
}): UseAdminGetOrganizationByIdResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const fetchOrganization = useCallback(async (): Promise<void> => {
    setResult((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await HttpClient.get<Organization | undefined>(
        ADMIN_ORGANIZATIONS_API_PATHS.GET_ORGANIZATION_BY_ID(organizationId)
      );

      setResult({ isLoading: false, organization: response ?? null });
    } catch (error) {
      enqueueSnackbar(
        extractApiErrorMessage(error, "Une erreur est survenue lors de la récupération de l'organisation"),
        { variant: 'error' }
      );
      setResult({ isLoading: false, organization: null });
    }
  }, [organizationId, enqueueSnackbar]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  return { ...result, fetchOrganization };
}
